import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { OrderContext } from '../../../context/OrderContext/order.context';
import { CUSTOMER_STORE_FLOW } from '../../../types/order-flow';
import {
	SypacAvatar,
	SypacButton,
	SypacText,
} from '@sypac/component-library-react';
import { T, useTranslate } from '@tolgee/react';
import { Person } from '../../../assets/Person';
import ProducerCompanyIcon from '../../../assets/ProducerCompanyIcon';
import { NumericFormat } from 'react-number-format';
import { Calendar } from '../../../assets/Calendar';
import { formatDate } from '../../../utils/time.util';
import DashedDivider from '../../DashedDivider/DashedDivider';
import Flag from 'react-world-flags';
import { Contract } from '../../../services/contracts.service';
import { numberTwoDecimal } from '../../../utils/number.util';
import { PlusIcon } from '../../../assets/PlusIcon';
import DeliveryAddresses from './DeliveryAddresses';
import { OrderTarget } from '../../OrdersTable/interfaces/OrderStatus.interface';
import { SYPAC_VAT } from '../../../constants';
import { ContractStatus } from '../interfaces/Contract.interface';
import { cn } from '../../../lib/utils';
import TooltipCircle from '../../../assets/TooltipCircle';
import { Tooltip } from 'react-tooltip';
import BankTransfer from '../../../assets/BankTransfer';

interface GeneralTabProps {
	contract: Contract;
	role?: OrderTarget;
	onCompanyClick?: (companyId: number) => void;
}

const GeneralTab: React.FC<GeneralTabProps> = ({
	contract,
	role = OrderTarget.admin,
	onCompanyClick,
}) => {
	const { t } = useTranslate();
	const navigate = useNavigate();
	const { setOrder, setAdditional } = useContext(OrderContext);

	const line = contract?.lines?.[0];
	const qtyTons = line?.contractQtyTons ?? 0;
	const markup = line?.markup ?? 0;

	const allowedOverdraftPercent = Number(line?.allowedOverdraftPercent ?? 0);

	const overdraftedQtyTons =
		allowedOverdraftPercent > 0
			? numberTwoDecimal(qtyTons * (1 + allowedOverdraftPercent / 100))
			: 0;

	const sellerPricePerTon = Number(line?.pricePerTonSeller ?? 0);
	const buyerPricePerTon = line?.pricePerTonBuyer * (1 + markup / 100);

	const basePricePerTon =
		role === OrderTarget.customer
			? buyerPricePerTon
			: role === OrderTarget.producer
			? sellerPricePerTon
			: 0;

	const contractCost = numberTwoDecimal(basePricePerTon * qtyTons);
	const pricePerTonWithMarkupAndVat = numberTwoDecimal(
		basePricePerTon * (1 + SYPAC_VAT),
	);

	const contractCostVatIncl = numberTwoDecimal(
		pricePerTonWithMarkupAndVat * qtyTons,
	);

	const sypacProfitRaw = (buyerPricePerTon - sellerPricePerTon) * qtyTons;

	const isSypacLoss = sypacProfitRaw < 0;
	const sypacAbsoluteProfit = numberTwoDecimal(Math.abs(sypacProfitRaw));

	return (
		<>
			<div className="flex flex-col gap-[20px] px-[40px] -mt-[15px] pt-[21px] pb-[20px] overflow-y-auto scrollbar-custom scroll-smooth">
				<div className="flex flex-col gap-4 p-4 bg-white border border-solid border-gray-10 rounded-[12px]">
					<SypacText variant="body-regular-medium">
						<p className="text-gray-60 text-[16px] leading-[22px]">
							<T keyName="generalTab.contractInfo">Contract Info</T>
						</p>
					</SypacText>

					<div className="flex flex-col gap-3">
						<div className="grid grid-cols-[220px,auto] items-start">
							<div className="flex items-center gap-3">
								<Person width={16} height={20} />
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40">
										<T keyName="generalTab.buyer">Buyer</T>
									</p>
								</SypacText>
							</div>

							<div className="relative flex flex-col gap-3">
								<SypacText variant="body-regular-medium">
									<p
										className={cn(
											role === OrderTarget.admin
												? 'text-cornflower-blue hover:underline cursor-pointer'
												: 'text-gray-80',
										)}
										onClick={() => {
											if (
												role === OrderTarget.admin &&
												onCompanyClick &&
												contract?.buyer?.id
											) {
												onCompanyClick(contract.buyer.id);
											}
										}}
									>
										{contract?.buyer?.name || ''}
									</p>
								</SypacText>

								<div className="flex gap-[10px] items-center">
									<SypacAvatar
										size="sm"
										initials={`${
											contract.buyerRepresentative?.firstName ?? ''
										} ${contract.buyerRepresentative?.lastName ?? ''}`.trim()}
									>
										{contract.buyerRepresentative?.photoUrl && (
											<img
												src={contract.buyerRepresentative?.photoUrl}
												alt={`${
													contract.buyerRepresentative?.firstName ?? ''
												} ${
													contract.buyerRepresentative?.lastName ?? ''
												}`.trim()}
												className="w-full h-full object-cover rounded-full"
											/>
										)}
									</SypacAvatar>
									<SypacText variant="body-regular-medium">
										<p className="text-gray-40 mt-[2px]">
											{`${contract.buyerRepresentative?.firstName ?? ''} ${
												contract.buyerRepresentative?.lastName ?? ''
											}`.trim()}
										</p>
									</SypacText>
								</div>

								{line?.ignoreCreditLimit && (
									<div className="flex gap-2">
										<BankTransfer color="#454545" width={20} height={20} />

										<div className="flex items-center gap-3">
											<SypacText variant="body-regular-medium">
												<p className="text-gray-80">
													<T keyName="generalTab.creditLimitDisabled">
														Credit limit disabled
													</T>
												</p>
											</SypacText>
											<span
												data-tooltip-id="contracts-general-tab"
												data-tooltip-content={
													role !== OrderTarget.customer
														? t(
																'generalTab.creditLimitTooltip',
																'Orders created from this contract',
														  )
														: t(
																'generalTab.creditLimitTooltipBuyer',
																'Orders you place from this contract',
														  )
												}
												className="p-0 m-0 mb-[2px] flex scale-[1.1]"
											>
												<TooltipCircle />
											</span>
										</div>
									</div>
								)}
							</div>
						</div>

						<DashedDivider dashLength={5} gapLength={5} />

						<div className="grid grid-cols-[220px,auto] items-start">
							<div className="flex items-center gap-3">
								<ProducerCompanyIcon width={20} height={22} />
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40">
										<T keyName="generalTab.seller">Seller</T>
									</p>
								</SypacText>
							</div>

							<div className="flex flex-col gap-3">
								<SypacText variant="body-regular-medium">
									<p
										className={cn(
											role === OrderTarget.admin
												? 'text-cornflower-blue hover:underline cursor-pointer'
												: 'text-gray-80',
										)}
										onClick={() => {
											if (
												role === OrderTarget.admin &&
												onCompanyClick &&
												contract?.seller?.id
											) {
												onCompanyClick(contract.seller.id);
											}
										}}
									>
										{contract?.seller?.name || ''}
									</p>
								</SypacText>

								<div className="flex gap-[10px] items-center">
									<SypacAvatar
										size="sm"
										initials={`${
											contract.sellerRepresentative?.firstName ?? ''
										} ${contract.sellerRepresentative?.lastName ?? ''}`.trim()}
									>
										{contract.sellerRepresentative?.photoUrl && (
											<img
												src={contract.sellerRepresentative?.photoUrl}
												alt={`${
													contract.sellerRepresentative?.firstName ?? ''
												} ${
													contract.sellerRepresentative?.lastName ?? ''
												}`.trim()}
												className="w-full h-full object-cover rounded-full"
											/>
										)}
									</SypacAvatar>
									<SypacText variant="body-regular-medium">
										<p className="text-gray-40 mt-[2px]">
											{`${contract.sellerRepresentative?.firstName ?? ''} ${
												contract.sellerRepresentative?.lastName ?? ''
											}`.trim()}
										</p>
									</SypacText>
								</div>
							</div>
						</div>

						<DashedDivider dashLength={5} gapLength={5} />

						<div className="grid grid-cols-[220px,auto] items-start">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40">
									<T keyName="generalTab.productName">Product name:</T>
								</p>
							</SypacText>

							<div className="flex flex-col gap-3">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										{contract?.lines?.[0]?.product?.name ||
											contract?.lines?.[0]?.productName ||
											''}
									</p>
								</SypacText>

								{contract?.lines?.[0]?.product.productBase ? (
									<div className="flex items-start gap-3">
										<SypacText variant="body-regular-medium">
											<p className="max-w-[180px] text-gray-40 whitespace-nowrap truncate">
												{contract?.lines?.[0]?.product.productBase.name || ''}
											</p>
										</SypacText>
										<div className="w-[2px] h-[2px] bg-gray-40 rounded-full mt-[9px]" />
										<div className="flex items-start gap-1">
											{contract?.lines?.[0]?.product.productBase.countryCode ? (
												<span className="mt-[2px]">
													<Flag
														className="object-cover border border-solid border-gray-10 rounded"
														code={
															contract?.lines?.[0]?.product.productBase
																.countryCode
														}
														width={20}
														height={14}
													/>
												</span>
											) : null}
											<SypacText variant="body-regular-medium">
												<p className="text-gray-40">
													{contract?.lines?.[0]?.product.productBase.address ||
														''}
												</p>
											</SypacText>
										</div>
									</div>
								) : null}
							</div>
						</div>

						<div className="grid grid-cols-[220px,auto] items-start">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40">
									<T keyName="generalTab.productType">Product type:</T>
								</p>
							</SypacText>

							<SypacText variant="body-regular-medium">
								<p className="text-gray-80">
									{`${contract?.lines?.[0]?.product?.type || ''}${
										contract?.lines?.[0]?.product?.size
											? ` (${contract?.lines?.[0]?.product?.size})`
											: ''
									}`}
								</p>
							</SypacText>
						</div>

						{role === OrderTarget.admin && (
							<div className="grid grid-cols-[220px,auto] items-start">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40">
										<T keyName="generalTab.sellerPrice">Seller price:</T>
									</p>
								</SypacText>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										<NumericFormat
											type="text"
											displayType="text"
											thousandSeparator=" "
											decimalSeparator="."
											value={numberTwoDecimal(sellerPricePerTon)}
											suffix={` ${
												contract?.countryCode === 'MDA' ? 'MDL' : 'PLN'
											}`}
										/>
									</p>
								</SypacText>
							</div>
						)}

						<div className="grid grid-cols-[220px,auto] items-start">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40">
									<T keyName="generalTab.contractQuantity">
										Contract quantity:
									</T>
								</p>
							</SypacText>

							<SypacText variant="body-regular-medium">
								<p className="text-gray-80">
									<NumericFormat
										type="text"
										displayType="text"
										thousandSeparator=" "
										decimalSeparator="."
										value={qtyTons}
									/>{' '}
									<T keyName="basicDetails.tons">tons</T>
								</p>
							</SypacText>
						</div>

						{allowedOverdraftPercent > 0 && (
							<div className="grid grid-cols-[220px,auto] items-start">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40">
										<T keyName="generalTab.overdraftedQuantity">
											Overdrafted quantity:
										</T>
									</p>
								</SypacText>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										<NumericFormat
											type="text"
											displayType="text"
											thousandSeparator=" "
											decimalSeparator="."
											value={overdraftedQtyTons}
										/>{' '}
										<T keyName="basicDetails.tons">tons</T>
									</p>
								</SypacText>
							</div>
						)}

						<DashedDivider dashLength={5} gapLength={5} />

						{role === OrderTarget.admin ? (
							<>
								<div className="grid grid-cols-[220px,auto] items-start">
									<SypacText variant="body-regular-medium">
										<p className="text-gray-40">
											<T keyName="generalTab.buyerPrice">Buyer price:</T>
										</p>
									</SypacText>

									<SypacText variant="body-regular-medium">
										<p className="text-gray-80">
											<NumericFormat
												type="text"
												displayType="text"
												thousandSeparator=" "
												decimalSeparator="."
												value={numberTwoDecimal(buyerPricePerTon)}
												suffix={` ${
													contract?.countryCode === 'MDA' ? 'MDL' : 'PLN'
												}`}
											/>
										</p>
									</SypacText>
								</div>

								<div className="grid grid-cols-[220px,auto] items-start">
									<SypacText variant="body-regular-medium">
										<p className="text-gray-40">
											<T keyName="generalTab.sypacMargin">SYPAC margin:</T>
										</p>
									</SypacText>

									<SypacText variant="body-regular-medium">
										<p className="text-gray-80">
											<NumericFormat
												type="text"
												displayType="text"
												thousandSeparator=" "
												decimalSeparator="."
												value={markup}
												suffix=" %"
											/>
										</p>
									</SypacText>
								</div>

								{line?.allowedOverdraftPercent && (
									<div className="grid grid-cols-[220px,auto] items-start">
										<SypacText variant="body-regular-medium">
											<p className="text-gray-40">
												<T keyName="generalTab.overdraft">Overdraft:</T>
											</p>
										</SypacText>

										<div className="flex items-center gap-3">
											<SypacText variant="body-regular-medium">
												<p className="text-gray-80">
													<NumericFormat
														type="text"
														displayType="text"
														thousandSeparator=" "
														decimalSeparator="."
														value={line?.allowedOverdraftPercent}
														suffix=" %"
													/>
												</p>
											</SypacText>
											<span
												data-tooltip-id="contracts-general-tab"
												data-tooltip-content={t(
													'generalTab.overdraftTooltip',
													'Overdraft: A percentage',
												)}
												className="p-0 m-0 mb-[2px] flex scale-[1.1]"
											>
												<TooltipCircle />
											</span>
										</div>
									</div>
								)}

								<div className="grid grid-cols-[220px,auto] items-start">
									<SypacText variant="body-regular-medium">
										<p className="text-gray-40">
											<T
												keyName={
													isSypacLoss
														? 'generalTab.sypacLoss'
														: 'generalTab.sypacProfit'
												}
											>
												{isSypacLoss ? 'SYPAC loss:' : 'SYPAC profit:'}
											</T>
										</p>
									</SypacText>

									<SypacText variant="body-regular-medium">
										<p className="text-gray-80">
											<NumericFormat
												type="text"
												displayType="text"
												thousandSeparator=" "
												decimalSeparator="."
												value={sypacAbsoluteProfit}
												prefix={isSypacLoss ? '-' : ''}
												suffix={` ${
													contract?.countryCode === 'MDA' ? 'MDL' : 'PLN'
												}`}
												allowNegative={false}
											/>
										</p>
									</SypacText>
								</div>
							</>
						) : (
							<>
								<div className="grid grid-cols-[220px,auto] items-start">
									<SypacText variant="body-regular-medium">
										<p className="text-gray-40">
											<T keyName="generalTab.productPrice">Product price:</T>
										</p>
									</SypacText>

									<SypacText variant="body-regular-medium">
										<p className="text-gray-80">
											<NumericFormat
												type="text"
												displayType="text"
												thousandSeparator=" "
												decimalSeparator="."
												value={numberTwoDecimal(basePricePerTon)}
												suffix={` ${
													contract?.countryCode === 'MDA' ? 'MDL' : 'PLN'
												} / t`}
											/>
										</p>
									</SypacText>
								</div>

								<div className="grid grid-cols-[220px,auto] items-start">
									<SypacText variant="body-regular-medium">
										<p className="text-gray-40">
											<T keyName="generalTab.contractCost">Contract cost:</T>
										</p>
									</SypacText>

									<SypacText variant="body-regular-medium">
										<p className="text-gray-80">
											<NumericFormat
												type="text"
												displayType="text"
												thousandSeparator=" "
												decimalSeparator="."
												value={contractCost}
												suffix={` ${
													contract?.countryCode === 'MDA' ? 'MDL' : 'PLN'
												}`}
											/>
										</p>
									</SypacText>
								</div>

								<div className="grid grid-cols-[220px,auto] items-start">
									<SypacText variant="body-regular-medium">
										<p className="text-gray-40">
											<T keyName="generalTab.contactCostVatIncl">
												Contact cost (VAT incl.):
											</T>
										</p>
									</SypacText>

									<SypacText variant="body-regular-medium">
										<p className="text-gray-80">
											<NumericFormat
												type="text"
												displayType="text"
												thousandSeparator=" "
												decimalSeparator="."
												value={contractCostVatIncl}
												suffix={` ${
													contract?.countryCode === 'MDA' ? 'MDL' : 'PLN'
												}`}
											/>
										</p>
									</SypacText>
								</div>
							</>
						)}

						<DashedDivider dashLength={5} gapLength={5} />

						<div className="grid grid-cols-[220px,auto] items-start">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40">
									<T keyName="generalTab.contractDateFrom">
										Contract date from:
									</T>
								</p>
							</SypacText>

							<div className="flex items-center gap-3 ml-[5px]">
								<span className="p-0 m-0 flex scale-[1.1]">
									<Calendar />
								</span>
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										{formatDate(contract?.startsAt)}
									</p>
								</SypacText>
							</div>
						</div>
						<div className="grid grid-cols-[220px,auto] items-start">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40">
									<T keyName="generalTab.contractDateTo">Contract date to:</T>
								</p>
							</SypacText>

							<div className="flex items-center gap-3 ml-[5px]">
								<span className="p-0 m-0 flex scale-[1.1]">
									<Calendar />
								</span>
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										{formatDate(contract?.expiresAt)}
									</p>
								</SypacText>
							</div>
						</div>
					</div>
				</div>

				{role === OrderTarget.customer &&
					![
						ContractStatus.canceled,
						ContractStatus.completed,
						ContractStatus.expired,
					].includes(contract.status as ContractStatus) && (
						<SypacButton
							variant="secondary"
							size="small"
							className="w-full mt-1"
						>
							<button
								type="button"
								className="w-full h-[42px] flex gap-3 items-center justify-center border-0 bg-primary-violet rounded-lg transition-all hover:bg-primary-violet/80"
								onClick={() => {
									setAdditional((prev) => ({
										...prev,
										orderFlow: CUSTOMER_STORE_FLOW.CONTRACT_LOCATION_FIRST,
									}));

									setOrder((prev) => ({
										...(prev ?? {}),
										contractData: contract,
										location: undefined,
									}));

									navigate('/store/location');
								}}
							>
								<PlusIcon color="white" />
								<SypacText variant="body-regular-medium">
									<p className="text-white mt-[2px]">
										<T keyName="generalTab.createOrder">Create order</T>
									</p>
								</SypacText>
							</button>
						</SypacButton>
					)}

				{contract.orders?.length ? (
					<DeliveryAddresses orders={contract.orders} />
				) : null}
			</div>
			<Tooltip
				place="top"
				id="contracts-general-tab"
				style={{
					backgroundColor: '#E8E8E8',
					color: '#000000',
					whiteSpace: 'normal',
					maxWidth: '248px',
				}}
			/>
		</>
	);
};

export default GeneralTab;
